// Statystyki (owner 2026-10-27, spec docs/superpowers/specs/2026-10-27-player-stats-design.md): GET /api/stats
// sends raw numbers; this turns them into the page's tabs of "{icon} label … value" rows, Minecraft-style.
// Pure (no Phaser): StatsModal.js draws what it returns. Labels are nouns, never "Posadziłeś/aś": players
// are girls and boys. Rows at 0 are shown (owner).
import { plural } from '../farm/vehicles.mjs';
import { ICONS } from './icons.mjs';

export const TABS = ['Farma', 'Zwierzęta', 'Pomaganie', 'Pojazdy', 'Gry', 'Ty', 'Rekordy'];
export const SINCE_NOTE = 'Część liczb liczymy od 28 października 2026.';

const MONTHS = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września',
  'października', 'listopada', 'grudnia'];

export function formatNumber(n) {
  return String(Math.round(n ?? 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function formatKm(m) {
  m = m ?? 0;
  if (m < 1000) return `${formatNumber(m)} m`;
  return `${(Math.round(m / 100) / 10).toFixed(1).replace('.', ',')} km`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// The fun comparisons: shown only when they round to at least one.
function compare(n, per, one, few, many, tail = '') {
  const k = Math.round((n ?? 0) / per);
  return k >= 1 ? `≈ ${formatNumber(k)} ${plural(k, one, few, many)}${tail}` : undefined;
}

function row(icon, label, value, note) {
  return note === undefined ? { icon, label, value } : { icon, label, value, note };
}

const fav = (f) => (f ? `${f.name} · ${formatNumber(f.n)}` : '—');

// Game rows: the four in the Salon Gier's order first, then any game that ships later (its id, a gamepad).
const GAME_ROWS = [['chess', 'crown', 'Szachy'], ['checkers', 'dot_black', 'Warcaby'],
  ['backgammon', 'dice', 'Tryktrak'], ['mancala', 'gamepad', 'Mancala']];

function gameRows(games = {}) {
  const known = GAME_ROWS.map(([id]) => id);
  const ids = [...known, ...Object.keys(games).filter((id) => !known.includes(id))];
  return ids.map((id, i) => {
    const g = games[id] ?? { played: 0, won: 0 };
    const [, icon, label] = GAME_ROWS.find(([gid]) => gid === id) ?? [id, 'gamepad', id];
    const value = i === 0
      ? `${formatNumber(g.played)} ${plural(g.played, 'gra', 'gry', 'gier')} · ${formatNumber(g.won)} ${plural(g.won, 'wygrana', 'wygrane', 'wygranych')}`
      : `${formatNumber(g.played)} · ${formatNumber(g.won)}`;
    return row(icon, label, value);
  });
}

export function statsRows(s) {
  const n = formatNumber;
  const rec = s.records ?? {};
  const crop = s.fav_crop && ICONS[`crop_${s.fav_crop.id}`] ? `crop_${s.fav_crop.id}` : 'plant';
  const tabs = {
    Farma: [
      row('seedling', 'Rośliny posadzone', n(s.planted)),
      row(crop, 'Ulubiona roślina', fav(s.fav_crop)),
      row('basket', 'Plony zebrane', n(s.harvested), compare(s.harvested, 50, 'taczka', 'taczki', 'taczek')),
      row('plant', 'Owoce z drzew', n(s.fruit)),
      row('hoe', 'Pola zaorane', n(s.plowed)),
      row('shovel', 'Pola wyczyszczone', n(s.cleared)),
      row('tractor', 'Pola maszynami', n(s.machine_cells)),
      row('fuel', 'Paliwo spalone', n(s.fuel_used)),
      row('coin', 'Zarobione na farmie', n(s.farm_coins)),
      row('star_full', 'Gwiazdki farmera', n(s.stars)),   // crop mastery stars, all crops together
    ],
    'Zwierzęta': [
      row('egg', 'Jajka zebrane', n(s.eggs), compare(s.eggs, 3, 'omlet', 'omlety', 'omletów')),
      row('milk', 'Mleko zebrane', n(s.milk)),
      row('food', 'Karmienia', n(s.feeds)),
      row('hand', 'Głaskania', n(s.pets)),
      row('paw', 'Ulubione zwierzę', fav(s.fav_animal)),
    ],
    Pomaganie: [
      row('seedling', 'Podlania u znajomych', n(s.help_water)),
      row('food', 'Karmienia u znajomych', n(s.help_feed)),
      row('hand', 'Głaskania u znajomych', n(s.help_pet)),
      row('heart', 'Pomoc od znajomych', n(s.help_received)),
      row('coin', 'Zarobione na pomaganiu', n(s.help_coins)),
      row('house', 'Odwiedziny u znajomych', n(s.visits)),
      row('canister', 'Kanistry wysłane / otrzymane', `${n(s.gifts_sent)} / ${n(s.gifts_received)}`),
      row('friends', 'Znajomi', n(s.friends)),
    ],
    Pojazdy: [
      row('bike', 'Rowerem', formatKm(s.bike_m), compare(s.bike_m, 100, 'boisko', 'boiska', 'boisk')),
      row('car', 'Autem', formatKm(s.car_m),
        compare(s.car_m, 300_000, 'wycieczka', 'wycieczki', 'wycieczek', ' do Krakowa')),
      row('fuel', 'Tankowania', n(s.refuels)),
      row('licence', 'Prawo jazdy od', formatDate(s.licence_iso)),
      row('book', 'Lekcje jazdy', n(s.lessons_done)),
      row('grad_cap', 'Podejścia do egzaminu', n(s.exam_attempts)),
    ],
    Gry: [
      ...gameRows(s.games),
      row('friends', 'Z ludźmi / z botem', `${n(s.games_people)} / ${n(s.games_bot)}`),
      row('handshake', 'Remisy', n(s.draws)),
      row('trophy', 'Najdłuższa seria wygranych', n(s.best_win_streak)),
    ],
    Ty: [
      row('clock', 'Grasz od', formatDate(s.joined_iso)),
      row('xp_star', 'Poziom', n(s.level)),
      row('sun', 'Dni w grze', n(s.days?.played)),
      row('star_full', 'Seria dni', n(s.days?.streak)),
      row('chat', 'Wiadomości na czacie', n(s.chat)),
      row('sofa', 'Meble postawione', n(s.furniture_placed)),
      row('dress', 'Ubrania kupione', n(s.clothes_bought)),
      row('money_out', 'Wydane monety', n(s.coins_spent)),
    ],
    Rekordy: [
      row('basket', 'Największy zbiór naraz', `${n(rec.best_harvest_coins)} {coin}`),
      row('heart', 'Najwięcej pomocy w dzień', n(rec.best_helps_day)),
      row('bike', 'Najdłuższa przejażdżka', formatKm(rec.best_ride_m)),
      row('sun', 'Najdłuższa seria dni', n(rec.best_streak)),
      row('trophy', 'Najdłuższa seria wygranych', n(s.best_win_streak)),
    ],
  };
  return Object.fromEntries(TABS.map((t) => [t, { rows: tabs[t], since: t !== 'Gry' }]));
}
